/**
 * sc-account-client — the small shared piece of the account system that runs
 * OUTSIDE the portal (favorites hearts, Request measurements) and the bits the
 * portal shares with it.
 *
 * - The session token: same key and order as assets/sc-account.js
 *   (`sc_account_token`, this tab's sessionStorage first, then the
 *   "keep me signed in" localStorage). Never logged, never in a URL.
 * - post(path, body): POST {apiBase}/{path} with a JSON body, credentials
 *   omitted (the App Proxy strips cookies anyway). Resolves { status, data },
 *   never rejects. A 401 invalid_token clears the token here exactly as the
 *   portal does (signedOutLocally) and announces `sc:account`. call() adds
 *   the HTTP status as `httpStatus` (the body's own `status` is left alone).
 * - The pending action (docs: PHASE2 decision 1): a logged-out shopper taps a
 *   heart or Request measurements, chooses Sign in / Create account, and the
 *   portal finishes that action after the session exists.
 *   sessionStorage `sc_account_pending` = { type: 'like'|'measure', productId,
 *   returnUrl, at }. Expires after 30 minutes.
 * - The flash: the portal's one-line result for the page it returns to.
 *   sessionStorage `sc_account_flash` = { type, ok, error, status, alreadyMeasured, at }.
 * - The likes cache: `likes/ids` for this token, kept 60s in sessionStorage
 *   (`sc_likes_ids`), bound to a hash of the token (never the token).
 *
 * Plain ES2019 IIFE, no build step. Exposes window.SCAccountClient.
 */
(function () {
  'use strict';
  if (window.SCAccountClient) return;

  var TOKEN_KEY = (window.SC_DEV_API_BASE ? 'scdev_account_token' : 'sc_account_token');
  var PENDING_KEY = (window.SC_DEV_API_BASE ? 'scdev_account_pending' : 'sc_account_pending');
  var FLASH_KEY = (window.SC_DEV_API_BASE ? 'scdev_account_flash' : 'sc_account_flash');
  var LIKES_KEY = (window.SC_DEV_API_BASE ? 'scdev_likes_ids' : 'sc_likes_ids');
  var PENDING_MAX = 30 * 60 * 1000;
  var FLASH_MAX = 5 * 60 * 1000;
  var LIKES_MAX = 60 * 1000;
  var ACCOUNT_URL = '/pages/account';

  function apiBase() {
    return String(window.SC_DEV_API_BASE || window.SC_ACCOUNT_API_BASE || '/apps/sc/account').replace(/\/+$/, '');
  }
  function getToken() {
    try { return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || null; } catch (e) { return null; }
  }
  function announce() {
    try { window.dispatchEvent(new CustomEvent('sc:account', { detail: { signedIn: !!getToken() } })); } catch (e) { /* old browser */ }
  }
  function clearToken() {
    try {
      localStorage.removeItem(TOKEN_KEY); sessionStorage.removeItem(TOKEN_KEY);
      // the portal's last-seen copy belongs to that session (sc-account.js wipeCache)
      sessionStorage.removeItem((window.SC_DEV_API_BASE ? 'scdev_account_cache' : 'sc_account_cache')); sessionStorage.removeItem((window.SC_DEV_API_BASE ? 'scdev_account_cache_fails' : 'sc_account_cache_fails'));
    } catch (e) { /* storage blocked */ }
    invalidateLikes();
    announce();
  }
  // cyrb53-style hash, the same as sc-account.js tokenHash: binding only.
  function tokenHash(s) {
    var h1 = 0xdeadbeef, h2 = 0x41c6ce57;
    for (var i = 0; i < s.length; i++) {
      var ch = s.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761); h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (h2 >>> 0).toString(36) + '.' + (h1 >>> 0).toString(36);
  }

  function post(path, body) {
    return fetch(apiBase() + '/' + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
      credentials: 'omit'
    }).then(function (r) {
      return r.text().then(function (t) {
        var j;
        try { j = t ? JSON.parse(t) : {}; } catch (e) { j = { ok: false, error: r.status >= 500 ? 'upstream_unavailable' : 'not_found' }; }
        if (typeof j !== 'object' || j === null) j = { ok: false, error: 'upstream_unavailable' };
        if (r.status === 404 && !j.ok && !j.error) j.error = 'not_found';
        return { status: r.status, data: j };
      });
    }).catch(function () { return { status: 0, data: { ok: false, error: 'network' } }; });
  }
  // post() with the session token added. A dead session clears the token (like
  // the portal) and answers { ok:false, error:'invalid_token', signedOut:true }.
  function call(path, body) {
    var token = getToken();
    var payload = Object.assign({}, body || {});
    if (token && payload.token === undefined) payload.token = token;
    return post(path, payload).then(function (res) {
      var d = res.data || {};
      d.httpStatus = res.status;
      if (!d.ok && res.status === 401 && d.error === 'invalid_token' && payload.token) {
        if (getToken() === payload.token) clearToken();
        d.signedOut = true;
      }
      return d;
    });
  }

  // ---- storage helpers ----------------------------------------------------
  function readJson(key) {
    try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (e) { return null; }
  }
  function writeJson(key, v) {
    try { sessionStorage.setItem(key, JSON.stringify(v)); return true; } catch (e) { return false; }
  }
  function drop(key) { try { sessionStorage.removeItem(key); } catch (e) { /* storage blocked */ } }

  // Only a same-site path ever becomes the way back (never //host, never a scheme).
  function safeReturn(u) {
    var v = typeof u === 'string' ? u.trim() : '';
    if (!/^\/(?![\/\\])/.test(v) || /[\u0000-\u001f]/.test(v)) return '/';
    return v.slice(0, 1000);
  }
  function validPending(p) {
    if (!p || (p.type !== 'like' && p.type !== 'measure')) return null;
    var id = Number(p.productId);
    if (!(id > 0) || Math.floor(id) !== id) return null;
    var age = Date.now() - (Number(p.at) || 0);
    if (!(age >= 0 && age < PENDING_MAX)) return null;
    return { type: p.type, productId: id, returnUrl: safeReturn(p.returnUrl), at: Number(p.at) };
  }
  var pending = {
    save: function (type, productId, returnUrl) {
      return writeJson(PENDING_KEY, { type: type, productId: Number(productId), returnUrl: safeReturn(returnUrl), at: Date.now() });
    },
    // The valid pending action, or null (an expired / malformed one is dropped).
    get: function () {
      var raw = readJson(PENDING_KEY);
      var p = validPending(raw);
      if (raw && !p) drop(PENDING_KEY);
      return p;
    },
    clear: function () { drop(PENDING_KEY); }
  };
  var flash = {
    set: function (f) { writeJson(FLASH_KEY, Object.assign({}, f, { at: Date.now() })); },
    take: function () {
      var f = readJson(FLASH_KEY);
      drop(FLASH_KEY);
      if (!f || !(Date.now() - (Number(f.at) || 0) < FLASH_MAX)) return null;
      return f;
    }
  };

  // ---- likes cache --------------------------------------------------------
  function readLikes() {
    var t = getToken();
    if (!t) return null;
    var c = readJson(LIKES_KEY);
    var age = c ? Date.now() - (Number(c.at) || 0) : -1;
    if (!c || c.th !== tokenHash(t) || !Array.isArray(c.ids) || !(age >= 0 && age < LIKES_MAX)) return null;
    return c.ids;
  }
  function writeLikes(ids) {
    var t = getToken();
    if (!t) return;
    writeJson(LIKES_KEY, { th: tokenHash(t), at: Date.now(), ids: ids });
  }
  function invalidateLikes() { drop(LIKES_KEY); }

  window.SCAccountClient = {
    accountUrl: ACCOUNT_URL,
    apiBase: apiBase,
    getToken: getToken,
    clearToken: clearToken,
    tokenHash: tokenHash,
    post: post,
    call: call,
    pending: pending,
    flash: flash,
    likes: { read: readLikes, write: writeLikes, invalidate: invalidateLikes },
    safeReturn: safeReturn
  };
  // sc-likes.js loads async too; if it ran first it is waiting for this.
  try { document.dispatchEvent(new Event('sc:account-client-ready')); } catch (e) { /* very old browser */ }
})();
